/**
 * @fileoverview Separator - Visual separator line component
 *
 * Purpose: Create visual separation between content sections
 * Dependencies: Radix UI Separator primitive
 * Used by: Menus, layouts, content sections, button groups
 *
 * Features:
 * - Horizontal or vertical orientation
 * - Accessible (proper ARIA attributes)
 * - Decorative by default (not part of navigation)
 * - Thin, subtle styling
 *
 * @example
 * // Horizontal separator (default)
 * <Separator />
 *
 * @example
 * // Vertical separator
 * <div className="flex h-5 items-center">
 *   <span>Item 1</span>
 *   <Separator orientation="vertical" />
 *   <span>Item 2</span>
 * </div>
 */

import * as React from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";

import { cn } from "@/utils/misc";

const Separator = React.forwardRef<
  React.ElementRef<typeof SeparatorPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>
>(
  (
    { className, orientation = "horizontal", decorative = true, ...props },
    ref,
  ) => (
    <SeparatorPrimitive.Root
      ref={ref}
      decorative={decorative}
      orientation={orientation}
      className={cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className,
      )}
      {...props}
    />
  ),
);
Separator.displayName = SeparatorPrimitive.Root.displayName;

export { Separator };
